import { Audit, Plan } from '@workspaces/types'
import { AssetDataGeoJsonLayer } from '../plan/filter/asset.type'
import { getAppMetadata } from '@/plan/metadata/metadata'
import AuditService from '@/services/audit/audit.service'
import { Filter, SimplePlanWithFilters } from '../plan/filter/filter.type'
import { filterAssetsFromPlan as filterAssets } from '../plan/filter/plan.helper'
import BrowserEnvironmentResolver from './environment.helper'
import { deserializePackage } from '@/plan/deserializeFilters'
import { serializePlanPackage } from '@/utils/serializePackage'
import { serializeLegend } from '@/utils/serializeLegend'
import { serializeFilters } from '@/utils/serializeFilters'

/**
 *
 * @param assets Assets to be analyzed
 * @param planWithFilters Sub filters that compose the filter defined by the user
 * @returns Assets that match the filter defined by the user and intersect with the assets passed as parameter
 */
export async function filterAssetsFromPlan(
  assets: AssetDataGeoJsonLayer[],
  planWithFilters: SimplePlanWithFilters,
  generateAuditEvent = true,
): Promise<AssetDataGeoJsonLayer[]> {
  const iteratedFilteredAssets = await filterAssets(
    BrowserEnvironmentResolver.getInstance(),
    assets,
    planWithFilters,
  )

  if (generateAuditEvent) {
    // Audit event for registering loading of a plan
    const params: SimplePlanWithFilters = {
      id: planWithFilters.id,
      name: planWithFilters.name,
      filters: planWithFilters.filters,
    }

    await AuditService.createEvent(
      getAppMetadata(),
      BrowserEnvironmentResolver.getInstance(),
      {
        type: Audit.AuditEventType.PlanLoadedUsage,
        params: { id: planWithFilters.id, name: planWithFilters.name },
      },
    )

    await AuditService.createEvent(
      getAppMetadata(),
      BrowserEnvironmentResolver.getInstance(),
      {
        type: Audit.AuditEventType.PlanLoaded,
        params,
      },
    )
  }

  return iteratedFilteredAssets
}

/**
 *
 * @param plan Plan
 * @returns Parsed plan
 */
export function deserializePlan(plan: Plan.Plan): Plan.Plan {
  return {
    ...plan,
    id: plan.id,
    package: deserializePackage(plan.package as string),
    editing: false,
  }
}

export function serializePlan(plan: Plan.Plan): Plan.Plan {
  let planSerialized = {} as Plan.Plan
  if (plan.package) {
    if (typeof plan.package === 'string') {
      console.warn(
        'Trying to serialize a plan that has packages filter already serialized',
      )
    } else {
      planSerialized = {
        ...plan,
        package: serializePlanPackage(plan.package),
      }
    }
  }

  if (plan.legend) {
    planSerialized = {
      ...planSerialized,
      legend: serializeLegend(plan.legend),
    }
  }

  if (plan.filters) {
    if (typeof plan.filters === 'string') {
      console.warn(
        'Trying to serialize a plan that has filters already serialized',
      )
    } else {
      planSerialized = {
        ...planSerialized,
        filters: serializeFilters(plan.filters),
      }
    }
  }
  delete planSerialized.editing

  return planSerialized
}

/**
 *
 * @param plans List of plans
 * @returns list of parsed plans
 */
export function deserializePlans(plans: Plan.Plan[]): Plan.Plan[] {
  const parsedPlans: Plan.Plan[] = plans.map((plan: Plan.Plan) => {
    return deserializePlan(plan)
  })
  return parsedPlans
}

export function getCountriesForCurrentPlan(filters: Filter[]): number[] {
  const countriesSelected = filters.map((filter) => {
    return filter.countries
  })

  const countries = [...new Set(countriesSelected.flat())]
  return countries
}
